import { useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import analyticsIconSrc from 'assets/analytics-icon.svg';
import remixSpinnerSrc from 'assets/remix-spinner.gif';

import styles from './styles.module.scss';

const MAX_ANALYTICS_FILES = 10;

const AnalyticsUpload = ({
  analyticsUploadState,
  analyticsFiles,
  onAnalyticsUpload,
  onAnalyticsRemove,
  disabled = false,
  compact = false,
}) => {
  const fileInputRef = useRef(null);

  const handleFileChange = e => {
    const { files } = e.target;

    if (files && files.length > 0) {
      onAnalyticsUpload(Array.from(files).slice(0, MAX_ANALYTICS_FILES));
      e.target.value = '';
    }
  };

  const firstFile = analyticsFiles?.[0];
  const extraCount = analyticsFiles ? analyticsFiles.length - 1 : 0;

  const isLoading = analyticsUploadState === 'uploading' || analyticsUploadState === 'in-progress';

  return (
    <div
      className={cn(styles['analytics-box'], {
        [styles['analytics-box--dashed']]: analyticsUploadState === null,
        [styles['analytics-box--analyzing']]: isLoading,
        [styles['analytics-box--done']]: analyticsUploadState === 'done',
        [styles['analytics-box--compact']]: compact,
      })}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Left: graphic + text */}
      <div className={styles['analytics-header']}>
        {analyticsUploadState === null && (
          <>
            <img
              src={analyticsIconSrc}
              alt=""
              width={68}
              height={56}
              className={styles['analytics-graphic-img']}
            />
            <span className={styles['analytics-text']}>
              Add your analytics images, such as heat or click maps
            </span>
          </>
        )}

        {isLoading && (
          <>
            <img
              src={remixSpinnerSrc}
              alt=""
              width={34}
              height={34}
              className={styles['analytics-graphic-img']}
            />
            <span className={styles['analytics-text']}>
              {analyticsUploadState === 'uploading'
                ? (analyticsFiles?.length > 1 ? 'Uploading your files...' : 'Uploading your file...')
                : (analyticsFiles?.length > 1
                  ? 'Analyzing files to update context'
                  : 'Analyzing file to update context')}
            </span>
          </>
        )}

        {analyticsUploadState === 'done' && firstFile && (
          <>
            <div
              className={styles['analytics-thumb']}
              style={{ backgroundImage: `url(${firstFile.previewUrl})` }}
            />
            <div className={styles['analytics-file-info']}>
              <div className={styles['analytics-filename-row']}>
                <span className={styles['analytics-filename']}>
                  {firstFile.name}
                </span>
                {extraCount > 0 && (
                  <span className={styles['analytics-extra-count']}>
                    {`+ ${extraCount} more`}
                  </span>
                )}
              </div>
              <span className={styles['analytics-status']}>
                Context updated
              </span>
            </div>
          </>
        )}
      </div>

      {/* Right: action button */}
      {analyticsUploadState === null && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className={cn(styles['analytics-btn'], {
            [styles['analytics-btn--disabled']]: disabled,
          })}
        >
          {disabled ? 'Loading context...' : 'Upload to update context'}
        </button>
      )}

      {isLoading && (
        <button
          type="button"
          disabled
          className={cn(styles['analytics-btn'], styles['analytics-btn--disabled'])}
        >
          In progress...
        </button>
      )}

      {analyticsUploadState === 'done' && (
        <button
          type="button"
          onClick={onAnalyticsRemove}
          className={styles['analytics-remove-btn']}
          aria-label="Remove analytics file"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5l10 10"
              stroke="#606776"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

AnalyticsUpload.propTypes = {
  analyticsUploadState: PropTypes.oneOf([null, 'uploading', 'in-progress', 'done']),
  analyticsFiles: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    previewUrl: PropTypes.string,
  })),
  onAnalyticsUpload: PropTypes.func.isRequired,
  onAnalyticsRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  compact: PropTypes.bool,
};

export default AnalyticsUpload;
